/**
 * Duel stage math — pure helpers for M2's in-world battle staging
 * (TODOS #25, revived by the 2026-08-02 litigation ruling). No Phaser.
 * Unit-tested in test/duel-stage.test.ts; WorldScene owns the thin
 * camera/sprite wrappers.
 *
 * The locked #25 spatial rules this module implements:
 *   - Duel slot: the player's lead steps INTO THE GAP between player
 *     and wild — facing direction first, 3-tile outward search, with
 *     the wild's own tile and blocked tiles skipped.
 *   - Camera: punch to DUEL_ZOOM on the FIXED midpoint between the
 *     lead's home tile and the wild's home tile, computed ONCE at
 *     battle start. Sprites may lunge off their tiles; the camera
 *     does not chase them.
 */

export interface CameraView {
  /** Left/top edge of the VISIBLE world rect — Phaser's cam.worldView,
   *  NOT scrollX/scrollY: under zoom ≠ 1 the camera shows the region
   *  centered on scroll + viewport/2, so the visible origin is
   *  scroll + (viewport − viewport/zoom)/2. Callers pass worldView
   *  so this module never re-derives Phaser's convention. */
  viewLeft: number;
  viewTop: number;
  zoom: number;
}

/** Target punch-in zoom multiplier over the current world zoom.
 *  #25 specced a 1.3-1.45 dial range "against real frames"; the phone
 *  dogfoods dialed it UP three times — 1.8, 2.2, then 3 (owner: "more
 *  zoom"). The pair-aware guardrail below is what actually protects
 *  framing, so this dial only ever loses to the guardrail — it never
 *  clips, no matter how far it climbs. */
export const DUEL_ZOOM_FACTOR = 3;

/** Pair-aware horizontal guardrail pad, in tiles beyond the slots'
 *  x-span: 1 per side for the 32px battler half-widths + 1 per side
 *  so the spectators (owners stand one tile behind/beside their
 *  leads) keep their sprites on screen. Spectator edges land exactly
 *  AT the frame edge when the cap binds; the integer floor in
 *  duelZoom is the margin. */
export const DUEL_VIEW_PAD_X_TILES = 3;

/** Vertical guardrail pad, in tiles beyond the slots' y-span, checked
 *  against the DUEL_FRAME_Y-biased effective height (the top half is
 *  the short side at 36%). 4 = battlers always fit with a half-tile+
 *  top margin (they stand 1 tile proud of their row); the rare
 *  above-line spectator (vertical-corridor far owner) may crop ~half
 *  a tile of hat at desktop-max zooms — 5 would protect them too, at
 *  the cost of capping desktop at 8 instead of 10. */
export const DUEL_VIEW_PAD_Y_TILES = 4;

/** Vertical framing bias: the duel midline anchors at this fraction
 *  of the viewport height instead of dead center, so the
 *  bottom-anchored battle chrome (log + action grid) sits BELOW the
 *  battlers (dead-center framing put the menus over the pair on a
 *  portrait phone — first phone dogfood, 2026-08-03). Lives here, not
 *  duel.ts, so the zoom guardrail can respect the shorter top half. */
export const DUEL_FRAME_Y = 0.36;

/** Effective duel zoom: the punch dial, clamped by the pair-aware
 *  framing guardrail on BOTH axes, floored to an INTEGER — the world
 *  base zoom is always integer (crispness invariant), and a
 *  fractional camera zoom under NEAREST filtering renders uneven
 *  texel widths (the "everything looks fuzzy" phone dogfood, round
 *  4) — and never below that base. Integer-in → integer-out.
 *
 *  `pairSpanTiles` is the |Δx|,|Δy| between the two battler SLOTS:
 *  close pairs (wild bumps, side-by-side trainer staging — the common
 *  cases) get the tight frame; wide corridor stagings auto-widen so
 *  nothing clips. */
export function duelZoom(
  baseZoom: number,
  viewportW: number,
  viewportH: number,
  tileSize: number,
  pairSpanTiles: { dx: number; dy: number },
): number {
  const needW = (Math.abs(pairSpanTiles.dx) + DUEL_VIEW_PAD_X_TILES) * tileSize;
  const needH = (Math.abs(pairSpanTiles.dy) + DUEL_VIEW_PAD_Y_TILES) * tileSize;
  // The midline sits at DUEL_FRAME_Y of the viewport; a symmetric
  // span fits iff it fits the SHORTER half twice — that's the
  // bias-effective height (2 · 0.36 · H).
  const frameH = viewportH * 2 * Math.min(DUEL_FRAME_Y, 1 - DUEL_FRAME_Y);
  const cap = Math.min(viewportW / needW, frameH / needH);
  return Math.max(baseZoom, Math.floor(Math.min(baseZoom * DUEL_ZOOM_FACTOR, cap)));
}

/** Snap a pinch multiplier to the nearest crisp stop: the applied
 *  camera zoom is an INTEGER (same NEAREST-filtering rationale as
 *  duelZoom), clamped to [base, base×6]. Returns the snapped ZOOM;
 *  divide by base for the multiplier to persist/read out — every
 *  pinch detent is then directly bakeable into DUEL_ZOOM_FACTOR. */
export function snapPinchZoom(baseZoom: number, mult: number): number {
  const raw = baseZoom * Math.min(6, Math.max(1, mult));
  return Math.min(baseZoom * 6, Math.max(baseZoom, Math.round(raw)));
}

/** World→screen for the main (zoomed, scrolling) camera — the inverse
 *  of getWorldPoint, which is the only direction the codebase had.
 *  Valid because Phaser.Scale.RESIZE keeps canvas px 1:1 with #game
 *  CSS px, so the result positions Zone-2 DOM directly. */
export function worldToScreen(
  wx: number,
  wy: number,
  cam: CameraView,
): { x: number; y: number } {
  return {
    x: (wx - cam.viewLeft) * cam.zoom,
    y: (wy - cam.viewTop) * cam.zoom,
  };
}

/** Fixed camera midpoint (world px) between the two battlers' home
 *  tiles, per the locked #25 rule: computed once, never interpolated
 *  during lunges. */
export function duelMidpoint(
  leadTile: { x: number; y: number },
  wildTile: { x: number; y: number },
  tileSize: number,
): { x: number; y: number } {
  return {
    x: ((leadTile.x + wildTile.x) / 2 + 0.5) * tileSize,
    y: ((leadTile.y + wildTile.y) / 2 + 0.5) * tileSize,
  };
}

/**
 * Where the lead materializes: the #25 spawn rule. Preference order —
 * the tile directly in front of the player (toward the wild), then
 * outward up to `searchDepth` tiles along that axis, skipping the
 * wild's own tile and anything `isBlocked`. Falls back to the
 * player's own tile (lead pops in place — the off-map fallback the
 * spec names) when nothing in the corridor is free.
 */
/**
 * Trainer-duel staging (#26): both leads need slots. With a real gap
 * (Manhattan ≥ 3) each lead steps into the corridor from its own side.
 * Adjacent/2-apart trainers (the LOS run-up always ends adjacent) get
 * SIDE-BY-SIDE staging: both leads step onto the same perpendicular
 * side of the player↔trainer axis, forming a duel line beside their
 * owners — the cartridge's "seconds step forward" read. Falls back to
 * stacking on the owners when every perpendicular is blocked.
 */
export function findTrainerDuelSlots(
  player: { x: number; y: number },
  trainer: { x: number; y: number },
  isBlocked: (x: number, y: number) => boolean,
): { mine: { x: number; y: number }; opp: { x: number; y: number } } {
  const dist =
    Math.abs(trainer.x - player.x) + Math.abs(trainer.y - player.y);
  if (dist >= 3) {
    const mine = findDuelSlot(player, trainer, isBlocked);
    const opp = findDuelSlot(trainer, player, isBlocked);
    // Corridor-branch honesty (Cornered ruling, 2026-08-03): either
    // search exhausting to its owner's tile, or both converging on
    // the SAME tile (one free tile mid-corridor found from both
    // ends), is not a staging — return the owner-stack sentinel so
    // the caller cuts to the classic modal instead of stacking.
    const degenerate =
      (mine.x === player.x && mine.y === player.y) ||
      (opp.x === trainer.x && opp.y === trainer.y) ||
      (mine.x === opp.x && mine.y === opp.y);
    if (!degenerate) return { mine, opp };
    return { mine: { ...player }, opp: { ...trainer } };
  }
  const horiz =
    Math.abs(trainer.x - player.x) >= Math.abs(trainer.y - player.y);
  const perps = horiz
    ? [
        { x: 0, y: -1 },
        { x: 0, y: 1 },
      ]
    : [
        { x: -1, y: 0 },
        { x: 1, y: 0 },
      ];
  for (const p of perps) {
    const mine = { x: player.x + p.x, y: player.y + p.y };
    const opp = { x: trainer.x + p.x, y: trainer.y + p.y };
    if (
      !isBlocked(mine.x, mine.y) &&
      !isBlocked(opp.x, opp.y) &&
      !(mine.x === opp.x && mine.y === opp.y)
    ) {
      return { mine, opp };
    }
  }
  return { mine: { ...player }, opp: { ...trainer } };
}

/** Predicates planWildStaging needs. Split three ways because the
 *  three actors have different rules: a battler slot cares about
 *  walls + roamers; the wild's hop additionally can't leave its grass
 *  patch; a PLAYER step is a raw positioning tween outside the step
 *  pipeline, so tiles that fire world machinery on entry (warps,
 *  coord-event triggers) are simply not offered rather than handled. */
export interface WildStagingChecks {
  /** Blocked for a battler slot: wall / water / NPC / roamer. */
  isBlocked: (x: number, y: number) => boolean;
  /** Wild startle-hop validity (RoamerSystem.canStartleTo): patch
   *  membership + isBlocked + other-roamer occupancy. */
  canHop: (x: number, y: number) => boolean;
  /** Player-step validity: walkable + no roamer + no warp + no
   *  coord trigger. */
  isPlayerSafe: (x: number, y: number) => boolean;
}

export type WildStagingPlan =
  | {
      kind: 'duel';
      /** The lead battler's tile. Post-plan this is NEVER the
       *  player's tile — the Cornered invariant. */
      slot: { x: number; y: number };
      /** Rung 1: the wild startle-hops here before the walk-in. */
      wildHop?: { x: number; y: number };
      /** Rungs 2–3: the player shuffles here first, vacating `slot`. */
      playerStep?: { x: number; y: number };
    }
  | { kind: 'modal' };

/**
 * The Cornered ruling ladder (owner ask 2026-08-03: "render all the
 * possibilities... and make a good ruling for each; it's ok to move
 * my character"). Adjacent bumps resolve in preference order:
 *
 *   1. Room behind the wild → it startle-hops back; lead takes its
 *      vacated tile. (The pre-existing behavior.)
 *   2. Wild cornered, room behind the player → the PLAYER gives
 *      ground one tile, eyes on the wild; lead takes the vacated
 *      tile. Head-on duel with the wall as backdrop.
 *   3. Pinched on the axis both ways → the player SIDESTEPS
 *      (north/west-first, the findTrainerDuelSlots convention); lead
 *      takes the vacated tile; the player watches from beside it.
 *   4. Sealed pocket (one-way ledge nook, roamer crowd) → NO in-world
 *      staging exists; the caller cuts to the classic frozen-modal
 *      battle. Never stack a battler on the player.
 *
 * Distant engagements (wild drifted during launch) keep findDuelSlot's
 * corridor search; an exhausted search (player-tile sentinel) is
 * 'modal' too — a duel across intervening geometry would lunge
 * through fences.
 *
 * Wild-sidestep was considered and REJECTED: whenever both player
 * moves fail it cannot manufacture a lead slot either, so it rescues
 * nothing rungs 2–3 don't already cover — and it surrenders the
 * cornered-against-the-wall shot.
 */
export function planWildStaging(
  player: { x: number; y: number },
  wild: { x: number; y: number },
  checks: WildStagingChecks,
): WildStagingPlan {
  const manhattan = Math.abs(wild.x - player.x) + Math.abs(wild.y - player.y);
  if (manhattan === 0) return { kind: 'modal' }; // contradiction guard
  if (manhattan >= 2) {
    const slot = findDuelSlot(player, wild, checks.isBlocked);
    if (!(slot.x === player.x && slot.y === player.y)) {
      return { kind: 'duel', slot };
    }
    // Straight-line contact at range 2 with the middle tile blocked:
    // this is the LEDGE-HOP engagement (the intervening tile is the
    // ledge itself — every downhill hop-into-grass contact lands
    // here, 19 such tiles on Routes 1–2). The wild startle-hops back
    // and the LEAD takes its vacated tile — leaping down at it while
    // the player stays perched above. Without this branch every
    // ledge engagement demoted to the classic modal (geometry
    // critic, 2026-08-03).
    if (manhattan === 2 && (wild.x === player.x || wild.y === player.y)) {
      const u = { x: Math.sign(wild.x - player.x), y: Math.sign(wild.y - player.y) };
      const back = { x: wild.x + u.x, y: wild.y + u.y };
      if (checks.canHop(back.x, back.y)) {
        return { kind: 'duel', slot: { ...wild }, wildHop: back };
      }
    }
    return { kind: 'modal' };
  }
  const d = { x: wild.x - player.x, y: wild.y - player.y };
  // Rung 1 — room behind the wild.
  const hop = { x: wild.x + d.x, y: wild.y + d.y };
  if (checks.canHop(hop.x, hop.y)) {
    return { kind: 'duel', slot: { ...wild }, wildHop: hop };
  }
  // Rung 2 — the player gives ground.
  const retreat = { x: player.x - d.x, y: player.y - d.y };
  if (checks.isPlayerSafe(retreat.x, retreat.y)) {
    return { kind: 'duel', slot: { ...player }, playerStep: retreat };
  }
  // Rung 3 — the player sidesteps.
  const perps =
    d.x !== 0
      ? [
          { x: 0, y: -1 },
          { x: 0, y: 1 },
        ]
      : [
          { x: -1, y: 0 },
          { x: 1, y: 0 },
        ];
  for (const p of perps) {
    const side = { x: player.x + p.x, y: player.y + p.y };
    if (checks.isPlayerSafe(side.x, side.y)) {
      return { kind: 'duel', slot: { ...player }, playerStep: side };
    }
  }
  // Rung 4 — sealed pocket.
  return { kind: 'modal' };
}

export interface TrainerStagingChecks {
  /** Blocked for a battler slot: wall / water / NPC / roamer. */
  isBlocked: (x: number, y: number) => boolean;
  /** Player back-step validity (same rules as the wild ladder). */
  isPlayerSafe: (x: number, y: number) => boolean;
  /** Trainer back-step validity — same predicate is fine (walkable,
   *  unoccupied, off machinery tiles, in-region). */
  isTrainerSafe: (x: number, y: number) => boolean;
}

export type TrainerStagingPlan =
  | {
      kind: 'duel';
      mine: { x: number; y: number };
      opp: { x: number; y: number };
      /** The player backs up here first, donating their tile. */
      playerStep?: { x: number; y: number };
      /** The trainer backs up here first, donating theirs. */
      trainerStep?: { x: number; y: number };
    }
  | { kind: 'modal' };

/**
 * Trainer staging with the corridor-retreat extension (owner ruling
 * 2026-08-03: "why can't either I or the trainer back up, or both, to
 * make room?"). findTrainerDuelSlots' geometry (corridor at range,
 * side-by-side at adjacency) stays the first preference; when it
 * returns the owner-stack sentinel — the 1-wide-corridor pinch —
 * BOTH owners give ground along the axis and the leads square off on
 * the vacated tiles: boxers returning to their corners while the
 * fighters meet in the middle. Only when walls prevent even that does
 * the plan go 'modal' (cut to the classic battle screen).
 */
export function planTrainerStaging(
  player: { x: number; y: number },
  trainer: { x: number; y: number },
  checks: TrainerStagingChecks,
): TrainerStagingPlan {
  const slots = findTrainerDuelSlots(player, trainer, checks.isBlocked);
  const stacked = slots.mine.x === player.x && slots.mine.y === player.y;
  if (!stacked) return { kind: 'duel', mine: slots.mine, opp: slots.opp };
  const dx = trainer.x - player.x;
  const dy = trainer.y - player.y;
  if (dx !== 0 && dy !== 0) return { kind: 'modal' }; // no axis (defensive)
  const d = { x: Math.sign(dx), y: Math.sign(dy) };
  const dist = Math.abs(dx) + Math.abs(dy);
  const pBack = { x: player.x - d.x, y: player.y - d.y };
  const tBack = { x: trainer.x + d.x, y: trainer.y + d.y };
  if (dist === 1) {
    // Adjacent: both must donate — leads take the two vacated tiles.
    if (
      checks.isPlayerSafe(pBack.x, pBack.y) &&
      checks.isTrainerSafe(tBack.x, tBack.y)
    ) {
      return {
        kind: 'duel',
        mine: { ...player },
        opp: { ...trainer },
        playerStep: pBack,
        trainerStep: tBack,
      };
    }
    return { kind: 'modal' };
  }
  if (dist === 2) {
    // One gap tile between: a single donor suffices. Prefer moving
    // the player ("it's ok to move my character").
    const gap = { x: player.x + d.x, y: player.y + d.y };
    if (checks.isBlocked(gap.x, gap.y)) return { kind: 'modal' };
    if (checks.isPlayerSafe(pBack.x, pBack.y)) {
      return { kind: 'duel', mine: { ...player }, opp: gap, playerStep: pBack };
    }
    if (checks.isTrainerSafe(tBack.x, tBack.y)) {
      return { kind: 'duel', mine: gap, opp: { ...trainer }, trainerStep: tBack };
    }
    return { kind: 'modal' };
  }
  // dist >= 3 arrived here via the degenerate-corridor sentinel: an
  // interior corridor tile is blocked, and no amount of backing up
  // un-blocks it — the leads would duel through a fence.
  return { kind: 'modal' };
}

/** Which way an actor standing on `from` turns to look at `to`:
 *  dominant axis, horizontal on ties — findDuelSlot's own corridor
 *  convention, so the turn always agrees with the tile the lead is
 *  about to step into. Null when the tiles coincide (nothing to face). */
export function facingTowardTile(
  from: { x: number; y: number },
  to: { x: number; y: number },
): 'up' | 'down' | 'left' | 'right' | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) return null;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
  return dy >= 0 ? 'down' : 'up';
}

export function findDuelSlot(
  player: { x: number; y: number },
  wild: { x: number; y: number },
  isBlocked: (x: number, y: number) => boolean,
  searchDepth = 3,
): { x: number; y: number } {
  const dx = Math.sign(wild.x - player.x);
  const dy = Math.sign(wild.y - player.y);
  // Cardinal-dominant axis toward the wild; diagonal contacts resolve
  // to the axis with the larger separation (ties prefer horizontal).
  const stepX = Math.abs(wild.x - player.x) >= Math.abs(wild.y - player.y) ? dx : 0;
  const stepY = stepX === 0 ? dy : 0;
  for (let i = 1; i <= searchDepth; i++) {
    const x = player.x + stepX * i;
    const y = player.y + stepY * i;
    if (x === wild.x && y === wild.y) break; // reached the wild — gap exhausted
    if (!isBlocked(x, y)) return { x, y };
  }
  return { x: player.x, y: player.y };
}
